import type { ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import { omit } from "lodash";
import { ChoiceElement as ChoiceElementComponent } from "@/react/components/FormRenderer/FormContent/ElementTypes/ChoiceElement";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { FieldElement } from "@/react/types/Element";
import { useBlocksContext } from "../../contexts/BlocksContext";

interface SingleChoiceBlockMeta {
  elementableId: number;
  elementId: number;
  fieldIndex: number;
  position: number;
}

interface Option {
  id: string;
  value: string;
}

export interface FormSingleChoiceBlockProps
  extends ComponentPropsWithRef<"div"> {
  blockInstanceMeta?: SingleChoiceBlockMeta;
  label?: string;
  options?: Option[];
  required?: boolean;
}

export const FormSingleChoiceBlock = forwardRef<
  HTMLDivElement,
  FormSingleChoiceBlockProps
>(
  (
    {
      label = "Single choice",
      required = false,
      options = [
        { id: "1", value: "Option 1" },
        { id: "2", value: "Option 2" },
        { id: "3", value: "Option 3" },
      ],
      className,
      blockInstanceMeta = {},
      ...rest
    },
    ref,
  ) => {
    const elementProps = omit(rest, ["blockPath"]);
    const { useFormRenderingContext } = useBlocksContext();
    const context = useFormRenderingContext();

    const optionValues = options.map(option => option.value);

    const element: FieldElement = {
      id: blockInstanceMeta.elementId ?? 0,
      elementable_id: blockInstanceMeta.elementableId ?? 0,
      position: blockInstanceMeta.position ?? 0,
      type: "field",
      attributes: {
        field_type: "single_choice",
        label,
        placeholder: null,
        required,
        hidden: false,
        options: optionValues,
      },
    };

    const errors =
      "backendErrors" in context ? (context.backendErrors ?? {}) : {};

    return (
      <div
        ref={ref}
        className={classNames("w-full", className)}
        {...elementProps}
      >
        <ChoiceElementComponent
          element={element}
          fieldIndex={blockInstanceMeta.fieldIndex ?? 0}
          errors={errors}
        />
      </div>
    );
  },
);

FormSingleChoiceBlock.displayName = "FormSingleChoiceBlock";
